import { useState } from "react";
import { optimizeGroceryList } from "../lib/gemini";
import { useAuth } from "../lib/AuthContext";
import { useUserStats } from "../lib/UserStatsContext";
import { useBadgeToast } from "./BadgeToast";
import { AIContent } from "./GIBadge";
import { getUserStats, recordGroceryOptimization, checkAndAwardBadges, checkUsageLimit, recordFeatureUsage } from "../lib/userStats";
import UsageLimitModal from "./UsageLimitModal";
import UsageIndicator from "./UsageIndicator";
import { BackIcon } from "./Icons";

interface GroceryOptimizerProps {
  onBack?: () => void;
}

export default function GroceryOptimizer({ onBack }: GroceryOptimizerProps) {
  const { user } = useAuth();
  const { dietaryPrefs } = useUserStats();
  const { showBadge } = useBadgeToast();
  const [groceryList, setGroceryList] = useState("");
  const [analysis, setAnalysis] = useState<string | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [limitInfo, setLimitInfo] = useState<{ currentUsage: number; isPremiumOnly: boolean }>({ currentUsage: 0, isPremiumOnly: false });
  const [usageRefreshKey, setUsageRefreshKey] = useState(0);

  const handleOptimize = async () => {
    if (!groceryList.trim() || !user) return;

    const usageCheck = await checkUsageLimit(user.uid, "groceryOptimizer");
    if (!usageCheck.allowed) {
      setLimitInfo({ currentUsage: usageCheck.currentUsage, isPremiumOnly: usageCheck.isPremiumOnly });
      setShowLimitModal(true);
      return;
    }

    setIsAnalyzing(true);
    try {
      const result = await optimizeGroceryList(groceryList, dietaryPrefs);
      setAnalysis(result);

      await recordFeatureUsage(user.uid, "groceryOptimizer");
      await recordGroceryOptimization(user.uid);
      setUsageRefreshKey((k) => k + 1);
      const stats = await getUserStats(user.uid);
      const newBadges = await checkAndAwardBadges(user.uid, stats);
      newBadges.forEach((badge) => showBadge(badge));
    } catch (error) {
      console.error("Error optimizing list:", error);
      setAnalysis("Sorry, I had trouble analyzing that list. Please try again.");
    } finally {
      setIsAnalyzing(false);
    }
  };

  const sampleLists = [
    {
      name: "Weekly Basics",
      list: "White bread\nPasta\nRice\nBananas\nPotatoes\nCereal\nMilk\nYogurt",
    },
    {
      name: "Snack Run",
      list: "Crackers\nGranola bars\nJuice\nChips\nCookies\nFruit snacks",
    },
  ];

  return (
    <div className="max-w-2xl mx-auto p-4">
      {onBack && (
        <button
          onClick={onBack}
          className="flex items-center gap-1 text-sm text-muted font-[family-name:--font-ui] mb-4 hover:text-text"
        >
          <BackIcon className="w-4 h-4" /> Back
        </button>
      )}
      <div className="section-label">Grocery Optimizer</div>
      <p className="text-sm text-muted mb-6">Get smarter swaps before you shop.</p>

      {!analysis ? (
        <div className="space-y-4">
          <div>
            <textarea
              value={groceryList}
              onChange={(e) => setGroceryList(e.target.value)}
              placeholder={"Paste or type your grocery list here...\n\nOne item per line, e.g.:\nWhite bread\nPasta\nRice\nBananas"}
              rows={10}
              className="border border-border rounded-md bg-bg text-text text-sm px-4 py-3 focus:border-accent outline-none resize-none w-full"
            />
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-muted font-[family-name:--font-ui]">Try a sample:</span>
            {sampleLists.map((sample, i) => (
              <button
                key={i}
                onClick={() => setGroceryList(sample.list)}
                className="text-accent text-xs font-[family-name:--font-ui] hover:underline"
              >
                {sample.name}
              </button>
            ))}
          </div>

          <button
            onClick={handleOptimize}
            disabled={!groceryList.trim() || isAnalyzing}
            className="w-full py-2.5 bg-accent text-white rounded-md font-[family-name:--font-ui] text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isAnalyzing ? (
              <>
                <div className="spinner spinner--sm"></div>
                Optimizing...
              </>
            ) : (
              "Optimize My List"
            )}
          </button>

          <div className="pt-2">
            <div className="text-xs text-muted font-[family-name:--font-ui] font-medium mb-2">What I'll do:</div>
            <ul className="text-sm text-muted space-y-1">
              <li>Flag high-GI items that might cause energy crashes</li>
              <li>Suggest better alternatives at similar prices</li>
              <li>Recommend additions for balanced meals</li>
            </ul>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="border-t border-border-light pt-4">
            <div className="text-xs text-muted font-[family-name:--font-ui] font-medium mb-2">Your original list:</div>
            <pre className="text-sm text-text whitespace-pre-wrap">{groceryList}</pre>
          </div>

          <AIContent content={analysis} />

          <button
            onClick={() => {
              setAnalysis(null);
              setGroceryList("");
            }}
            className="w-full py-2.5 border border-border rounded-md text-text font-[family-name:--font-ui] text-sm hover:bg-bg"
          >
            Optimize Another List
          </button>
        </div>
      )}

      {/* Usage Indicator */}
      <div className="mt-4 flex justify-center">
        <UsageIndicator feature="groceryOptimizer" compact onLimitReached={() => setShowLimitModal(true)} refreshKey={usageRefreshKey} />
      </div>

      {/* Usage Limit Modal */}
      <UsageLimitModal
        isOpen={showLimitModal}
        onClose={() => setShowLimitModal(false)}
        feature="groceryOptimizer"
        currentUsage={limitInfo.currentUsage}
        isPremiumOnly={limitInfo.isPremiumOnly}
      />
    </div>
  );
}
